/**
 * History popover — clock-icon button that opens a small floating
 * window listing recent Agent-of-Agents sessions. Includes:
 *
 *   - Search input at the top
 *   - First 5 sessions visible
 *   - Scroll down to reveal the rest (incremental)
 *   - Click a session to switch to it
 *   - "⋯" on a row → Delete: the regular chat list's own menu (same classes,
 *     icon and label, Sidebar.css); the page asks for confirmation.
 *
 * Uses AntD's Popover for placement + outside-click handling.
 */

import React, { useEffect, useRef, useState } from 'react';
import { formatRelativeTime } from '@/utils/relativeTime';
import { useLanguage } from '@/hooks/useLanguage';
import { Popover, Tooltip } from 'antd';
import type { AoAConversation } from '../types';
import '@/components/layout/Sidebar.css';
import './HistoryPopover.css';
import { mirrorPlacement } from '@/utils/rtl';
import { ConversationExportMenuItems } from '@modules/conversations/components/ConversationExportMenu';
import { useStayInViewport } from '@/hooks/useStayInViewport';

interface HistoryPopoverProps {
  sessions: AoAConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
  /** True when the backend has more pages beyond what's currently in `sessions`. */
  hasMore?: boolean;
  /** True while the next page is fetching. */
  loadingMore?: boolean;
  /** Triggered when the user scrolls near the bottom AND `hasMore`.
   *  Parent owns the offset/cursor — popover just signals "I need more". */
  onLoadMore?: () => void;
  /** Controlled search query (lives on the parent so paginated fetches
   *  carry the same q across load-more rounds). */
  query?: string;
  /** Notify parent of a query change. Parent debounces and refetches
   *  page 1 of the filtered set. */
  onQueryChange?: (q: string) => void;
  /** Delete was chosen on a row. The page confirms and deletes; the popover closes. */
  onDelete?: (session: AoAConversation) => void;
}

const MENU_WIDTH = 160;

const HistoryContent: React.FC<{
  sessions: AoAConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  close: () => void;
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  onDelete?: (session: AoAConversation) => void;
}> = ({ sessions, activeId, onSelect, close, loading, hasMore, loadingMore, onLoadMore, query, onQueryChange, onDelete }) => {
  const { t, direction } = useLanguage();
  const listRef = useRef<HTMLDivElement | null>(null);
  // The row whose "⋯" menu is open, and where its (fixed) dropdown sits —
  // the same placement the sidebar's conversation menu uses.
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuDropdownRef = useStayInViewport<HTMLDivElement>();

  useEffect(() => {
    if (!menuOpenId) return;
    const onDocClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.conversation-menu-container')) setMenuOpenId(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpenId(null); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpenId]);

  const toggleMenu = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (menuOpenId === id) {
      setMenuOpenId(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // The dropdown opens toward the row's text: under the button, aligned to
    // its end edge in LTR and its start edge in RTL.
    const left = direction === 'rtl' ? rect.left : rect.right - MENU_WIDTH;
    setMenuPosition({ top: rect.bottom + 4, left: Math.max(8, left) });
    setMenuOpenId(id);
  };

  // Filtering is now SERVER-SIDE. The parent receives the query, debounces,
  // and refetches page 1 of the filtered set. The popover renders whatever
  // `sessions` currently holds — no client-side filter is applied here.
  // This means searches paginate cleanly: the user gets the matches that
  // fit in one page, scrolls, the parent fetches the next page of matches.
  // Memory stays bounded regardless of haystack size (user instruction:
  // "only as it loads").
  const shown = sessions;

  // Reset scroll when the query changes — new search starts from the top.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = 0;
  }, [query]);

  // Hit-bottom → fetch next backend page of the CURRENT result set
  // (filtered or not — server applies the same `q` to every page).
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // A fixed dropdown would stay put while its row scrolls away.
    if (menuOpenId) setMenuOpenId(null);
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      if (hasMore && !loadingMore && onLoadMore) {
        onLoadMore();
      }
    }
  };

  return (
    <div className="aoa-history">
      <div className="aoa-history-search">
        <svg className="aoa-history-search-icon" width="13" height="13" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <line x1="20" y1="20" x2="16.65" y2="16.65" />
        </svg>
        <input dir="auto"
          autoFocus
          className="aoa-history-search-input"
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t('metaAgent.searchSessions')}
        />
      </div>

      <div
        ref={listRef}
        className="aoa-history-list"
        onScroll={onScroll}
        data-testid="aoa-history-list"
      >
        {loading && sessions.length === 0 && (
          <div className="aoa-history-empty">{t('common.loading')}</div>
        )}
        {!loading && shown.length === 0 && (
          <div className="aoa-history-empty" dir="auto">
            {query.trim() ? t('metaAgent.history.noSessionsMatch') : t('metaAgent.history.noSessionsYet')}
          </div>
        )}
        {shown.map((s) => {
          const isActive = s.conversation_id === activeId;
          const menuOpen = menuOpenId === s.conversation_id;
          return (
            <div
              key={s.conversation_id}
              className={`aoa-history-item ${isActive ? 'is-active' : ''}`}
              data-testid="aoa-history-item"
            >
              <button
                type="button"
                className="aoa-history-item-open"
                onClick={() => {
                  onSelect(s.conversation_id);
                  close();
                }}
                title={s.conversation_title || ''}
              >
                <span className="aoa-history-item-title" dir="auto">
                  {s.conversation_title || t('metaAgent.untitledSession')}
                </span>
                {s.last_message_at && (
                  <span className="aoa-history-item-time">
                    {formatRelativeTime(s.last_message_at)}
                  </span>
                )}
              </button>
              <div className={`conversation-menu-container ${menuOpen ? 'menu-open' : ''}`}>
                <button
                  type="button"
                  className="conversation-menu-btn"
                  onClick={(e) => toggleMenu(e, s.conversation_id)}
                  title={t('sidebar.options')}
                  aria-label={t('sidebar.options')}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  data-testid="aoa-history-item-menu"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <circle cx="5" cy="12" r="1.5" fill="currentColor" />
                    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                    <circle cx="19" cy="12" r="1.5" fill="currentColor" />
                  </svg>
                </button>
                {menuOpen && (
                  <div
                    ref={menuDropdownRef}
                    className="conversation-menu-dropdown"
                    role="menu"
                    style={{ top: menuPosition.top, left: menuPosition.left }}
                  >
                    <ConversationExportMenuItems conversationId={s.conversation_id} scope="meta"
                                                 onDone={() => { setMenuOpenId(null); close(); }} />
                    {onDelete && (
                      <button
                        type="button"
                        role="menuitem"
                        className="conversation-menu-item delete"
                        data-testid="aoa-history-item-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenId(null);
                          close();
                          onDelete(s);
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                          <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" fill="none" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" fill="none" />
                        </svg>
                        {t('vault.delete')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {/* Footer signals from the backend pagination state. Works the
            same whether the user is browsing or searching — the server
            applies the current `q` to every page, so `hasMore` is
            accurate for both. */}
        {loadingMore ? (
          <div className="aoa-history-more">{t('common.loadingMore')}</div>
        ) : hasMore ? (
          <div className="aoa-history-more">{t('common.scrollForMore')}</div>
        ) : null}
      </div>
    </div>
  );
};

export const HistoryPopover: React.FC<HistoryPopoverProps> = ({
  sessions,
  activeId,
  onSelect,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  query = '',
  onQueryChange,
  onDelete,
}) => {
  const { t, direction } = useLanguage();
  const [open, setOpen] = useState(false);
  // Fallback local-state if the parent doesn't provide controlled
  // query handling — this preserves backward compat with callers
  // that haven't been updated, but the AoA page uses the controlled
  // path which routes to the backend.
  const [localQuery, setLocalQuery] = useState('');
  const effectiveQuery = onQueryChange ? query : localQuery;
  const effectiveOnQueryChange = onQueryChange ?? setLocalQuery;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement={mirrorPlacement(direction, 'bottomLeft')}
      arrow={false}
      overlayClassName="aoa-history-popover"
      content={
        <HistoryContent
          sessions={sessions}
          activeId={activeId}
          onSelect={onSelect}
          close={() => setOpen(false)}
          loading={loading}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
          query={effectiveQuery}
          onQueryChange={effectiveOnQueryChange}
          onDelete={onDelete}
        />
      }
    >
      {/* Hidden while the popover is open: it sat on the "Search sessions…" box. */}
      <Tooltip
        title={t('metaAgent.sessionHistory')}
        placement="bottom"
        overlayClassName="aoa-tooltip"
        open={open ? false : undefined}
      >
        <button
          type="button"
          className={`aoa-header-icon-btn ${open ? 'is-open' : ''}`}
          aria-label={t('metaAgent.sessionHistory')}
          data-testid="aoa-history-button"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </button>
      </Tooltip>
    </Popover>
  );
};
